<script setup>
import { inject, ref, computed, watch, onMounted, onServerPrefetch } from 'vue'
import { action } from '@/utils/gtm'
import { getArticleRelation } from '@/api/article.js'
import ArticleGhostCard from '../../../components/GhostCard/index.vue'
import { checkVideo } from '@/utils'

const props = defineProps({
  id: { type: [String, Number], default: null },
  fixed: { type: Boolean, default: false }
})

const windowHeight = inject('height')
const height = computed(() =>
  windowHeight.value ? windowHeight.value - 64 - (props.fixed ? 32 : 70) - 32 - 52 : null
) // 可视高度 - header高度 - 顶部间距 - 底部间距 - 标题高度
const size = inject('size')
const scrollTop = ref(0)
const onscroll = (e) => {
  let obj = {
    scrollTop: e?.target?.scrollTop,
    scrollHeight: e?.target?.scrollHeight,
    clientHeight: e?.target?.clientHeight
  }
  let distance = obj.scrollHeight - obj.scrollTop - obj.clientHeight
  if (distance <= 200) onload() // 无限滚动加载
  scrollTop.value = obj.scrollTop || 0
}

// 滚动条 - 有滚动行为则显示，滚动停止则隐藏
let timer = null
const clearTimer = () => clearTimeout(timer)
const scrollbarShow = ref(false)
watch(scrollTop, () => {
  clearTimer()
  scrollbarShow.value = true
  timer = setTimeout(() => {
    scrollbarShow.value = false
  }, 500)
})

const loading = ref(false)
const list = ref([])
const headInfo = computed(() => list.value[0])
const slist = computed(() => list.value.slice(1))
const isEmpty = computed(() => list.value.length === 0)
const finished = ref(false) // 最後頁完成狀態
const page = ref(1)
const onload = async (mode) => {
  const isInit = mode === 'init'
  if (loading.value || finished.value) return
  loading.value = true
  try {
    const limit = 20
    const res = await getArticleRelation({
      article_id: props.id,
      page: page.value,
      limit
    })
    if (res.code !== 0) throw new Error(res.message)
    let newsList = res.data?.list || []
    if (isInit) list.value = newsList
    else list.value.push(...newsList)

    if (newsList.length < limit) finished.value = true
    else page.value++
    loading.value = false
  } catch (error) {
    console.warn(error)
    loading.value = false
  }
}

onMounted(() => onload('init'))

onServerPrefetch(async () => {
  // await onload('init')
})

const layerData = inject('layerData')
const tracking = (item) => {
  action({
    ...layerData.value,
    eventID: 'B014',
    page_title: item.title || '',
    screen_class: item.title || '',
    eventCategory: 'sub_category',
    eventLabel: item.title || '',
    screenLabel: item.link || ''
  })
}
</script>

<template>
  <div v-if="!isEmpty" class="tvb-article-relation tvb-hairline--surround">
    <div class="tvb-article-relation-tit">相關文章</div>
    <n-scrollbar
      v-if="!isEmpty"
      trigger="hover"
      :style="`max-height: ${height > 1200 ? 1200 : height}px`"
      :class="[scrollTop > 0 ? 'tvb-hairline--top' : '', { 'scrollbar-show': scrollbarShow }]"
      :on-scroll="onscroll"
    >
      <div
        class="tvb-article-relation_scroll-wrap"
        :style="loading ? '--scroll-wrap-padding-bottom: 16px' : ''"
      >
        <article-ghost-card
          v-if="headInfo"
          :size="size"
          :img="headInfo.image_info"
          :isVideo="checkVideo(headInfo)"
          :playIcon="{ size: 40 }"
          :title="headInfo.title"
          :subtitle="headInfo.category_name"
          :date="headInfo.publish_at"
          :link="{ url: headInfo.link, rel: headInfo.link_rel, target: headInfo.link_target }"
          titleTag="div"
          backgroundc="#F5F6F7"
          @tracking="tracking(headInfo)"
        ></article-ghost-card>
        <div v-if="list.length > 1" class="tvb-article-relation-divider"></div>
        <tvb-article-card-lateral
          class="tvb-article-relation_scroll-wrap-article-card"
          v-for="(item, index) in slist"
          :key="index"
          :info="item"
          title-tag="p"
          backgroundc="#F5F6F7"
          @tracking="tracking(item)"
        />

        <div v-if="loading" class="tvb-loading-box loading-box"><n-spin /></div>
        <div v-if="finished && list.length >= 20" class="text-center finished-tip">
          - 暫無更多數據 -
        </div>
      </div>
    </n-scrollbar>
  </div>
</template>

<style lang="scss" scoped>
.tvb-article-relation {
  box-sizing: border-box;
  width: 100%;
  max-width: 400px;
  // max-height: 1200px;
  background: #f8f9fa;
  border-radius: 8px;
  --tvb-hairline-border-radius: calc(2 * 8px);
  --scroll-wrap-padding: 20px;
  --scroll-wrap-padding-bottom: var(--scroll-wrap-padding);
  padding-left: var(--scroll-wrap-padding);

  .tvb-article-relation-tit {
    font-size: 18px;
    font-weight: 500;
    color: #333333;
    line-height: 24px;
    letter-spacing: 0.5px;
    padding: 16px 0 12px;
  }

  .tvb-article-relation-divider {
    height: 1px;
    background-color: rgba(163, 178, 188, 0.35);
    margin: 16px 0;
  }

  :deep(.n-scrollbar-container) {
    overscroll-behavior: contain; // 不会对相邻的滚动区域进行滚动
  }

  // 滚动条样式
  :deep(.n-scrollbar) {
    --n-scrollbar-width: 4px !important;
    --n-scrollbar-color: #ced5db !important;
    --n-scrollbar-color-hover: #ced5db !important;
    --n-scrollbar-border-radius: 2px !important;
    max-height: 1200px - 52px;
    --tvb-hairline-border-width: 2px;
    --tvb-hairline-border-radius: 0;
    &.tvb-hairline--top::after {
      margin-right: 27px;
    }
    & > .n-scrollbar-rail.n-scrollbar-rail--vertical,
    & + .n-scrollbar-rail.n-scrollbar-rail--vertical {
      top: 0px;
      right: 8px;
      bottom: var(--scroll-wrap-padding);
    }
    .n-scrollbar-rail__scrollbar {
      visibility: hidden;
    }
    &.scrollbar-show .n-scrollbar-rail__scrollbar {
      visibility: visible;
    }
  }

  .tvb-article-relation_scroll-wrap {
    padding-right: var(--scroll-wrap-padding);
    padding-bottom: var(--scroll-wrap-padding-bottom);
    .loading-box {
      padding-top: 16px;
      :deep(.n-spin) {
        --n-size: 16px !important;
      }
    }

    .tvb-article-relation_scroll-wrap-article-card {
      :deep(.tvb-article-card-lateral-title) {
        @include line-text-ellipsis(2);
      }
      :deep(.tvb-article-card-lateral-description) {
        display: none;
      }
    }

    .finished-tip {
      font-size: 13px;
      color: #818d95;
      line-height: 18px;
      margin-top: 16px;
    }

    :deep(.tvb-article-ghost-card) {
      --gc-cover-radio: 56.3888888%;
    }

    :deep(.tvb-article-card-lateral) {
      height: 72px;
      & + .tvb-article-card-lateral {
        margin-top: 16px;
      }
      .tvb-article-card-lateral-image {
        min-width: 128px;
        width: 128px;
        aspect-ratio: calc(128px / 72px);
        .tvb-image_play-icon {
          width: 32px !important;
          height: 32px !important;
          font-size: calc(32px / 2) !important;
          border-radius: calc(32px / 2) !important;
        }
        .tvb-image_placeholder {
          width: 77px;
          height: 28px;
        }
      }
      .tvb-article-card-lateral-info {
        margin-left: 10px;
        width: auto;
        flex: 1;
        min-width: 0;
        .tvb-article-card-lateral-title {
          font-size: 16px;
          line-height: 22px;
          font-weight: 400;
        }
        .tvb-article-card-lateral-category_name-publish_at {
          padding-bottom: 6px;
          .tvb-article-card-lateral-category_name,
          .tvb-article-card-lateral-publish_at {
            font-size: 13px;
            line-height: 18px;
            letter-spacing: 0;
          }

          .tvb-article-card-lateral-publish_at {
            margin-right: 0;
          }
        }
      }
    }
  }
}
</style>
